import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { APITypes, Dynamo, MedicalDataTypes } from '../../primary-record-types';
import urls from '../../utils/urls';
import useNotify from '../useNotify';
import { getIntl } from '../../utils/intl';
import { appAPI } from '../../utils/api';
import { NoteCommentMetadata, NotesList } from '../../primary-record-types/apiTypes/notesService';
import { NoteDestination, resourceNoteEntityTypeToResourceCategory } from '../../components/shared/Notes/utils/types';
import { useNoteQueryArgsResourceCategory } from './useHasNotePermission';
import { useLoadedUser } from './useUser';
import { useExplicitAppType } from '../useAppType';
import useOrganization from './useOrganization';
import {
    getCareTeamMemberNotesCacheKey,
    getNoteCacheKey,
    onNotesUpdate,
    getProfileNotesCacheKey,
    getResourceCategoryNotesCacheKey,
    getResourceNotesCacheKey,
    getUserNotesCacheKey,
    onNoteComment,
    onClientRecordNoteCopiesChanged,
    onNoteLinksChanged,
    getOrganizationNotesCacheKey,
    getOrganizationClientRecordNotesCacheKey,
    onOrgNoteCopiedToProfiles,
} from './cacheKeys';
import useQueryCallbacks, { useQueryErrorNotify } from '../useQueryCallbacks';
import { NoteEditorProps, SubmitArgs } from '../../components/shared/Notes/NotesEditor';
import React from 'react';

const intl = getIntl('UseNotesNotes', {
    createSuccess: 'Note created successfully',
    createFailure: 'Note creation failed',
    updateSuccess: 'Note updated successfully',
    updateFailure: 'Note update failed',
    deleteSuccess: 'Note deleted successfully',
    deleteFailure: 'Note deletion failed',
    deleteNotesSuccess: 'Notes deleted successfully',
    deleteNotesFailure: 'Notes deletion failed',
    notesLoadFailure: 'Failed to load notes',
    noteLoadFailure: 'Failed to load note',
    noteFileLoadFailure: 'Failed to load note file',
    commentFailed: 'Failed to comment on note',
    copyNoteSuccess: 'Note copied successfully',
    copyNoteFailure: 'Note copy failed',
    copyNoteToClientsSuccess: 'Note copied to clients successfully',
    copyNoteToClientsFailure: 'Copying note to clients failed',
    copyNoteToClientSuccess: 'Note copied to client successfully',
    copyNoteToClientFailure: 'Copying note to client failed',
    sendNoteEmailSuccess: 'Note sent successfully',
    sendNoteEmailFailure: 'Failed to send note',
    linkNoteToClientRecordSuccess: 'Note saved to client record',
    linkNoteToClientRecordAlreadyLinked: 'Note is already saved to the client record',
    linkNoteToClientRecordNoClientRecord: 'Your organization does not have a client record for this profile',
    linkNoteToClientRecordFailure: 'Failed to save note to client record',
});

export type UserNoteQueryArgs = {
    entityType: 'user';
};

export type OrganizationNoteQueryArgs = {
    entityType: 'organization';
    organizationId: string;
};

export type OrganizationClientRecordNoteQueryArgs = {
    entityType: 'organization-client-record';
    organizationId: string;
    organizationClientRecordId: string;
};

export type MedicalProfileNoteQueryArgs = {
    entityType: 'medical-profile';
    medicalProfileId: string | null;
};

export type CareTeamNoteQueryArgs = {
    entityType: 'care-team';
    medicalProfileId: string;
};

export type CareTeamMemberNoteQueryArgs = {
    entityType: 'care-team-member';
    entityId: string;
    medicalProfileId: string;
};
export type ResourceNoteQueryArgs = {
    entityType: Dynamo.ResourceNoteEntityType;
    medicalProfileId: string;
    resourceId: string;
    resourceGroupIds: string[];
};

export type ResourceCategoryNoteQueryArgs = {
    entityType: MedicalDataTypes.PrimaryRecordResourceCategory;
    medicalProfileId: string;
};

export type NoteQueryArgs =
    | UserNoteQueryArgs
    | MedicalProfileNoteQueryArgs
    | CareTeamMemberNoteQueryArgs
    | ResourceNoteQueryArgs
    | ResourceCategoryNoteQueryArgs
    | CareTeamNoteQueryArgs
    | OrganizationNoteQueryArgs
    | OrganizationClientRecordNoteQueryArgs;

export type UseNotesArgs = {
    noteId?: string;
    /**
     * Where to query the notes from
     */
    noteQueryArgs?: NoteQueryArgs;
    onUpdateSuccess?: (drySubmit: boolean) => void;
    onDeletionSuccess?: () => void;
    onCreationSuccess?: (drySubmit: boolean) => void;
    /**
     * On create, but returns the created note
     */
    onCreateNoteResponse?: (createdNote: APITypes.notesService.NoteDetails) => void;
    onNoteLoadFailure?: () => void;
    onLoadNoteFile?: (url: string) => void;
    onCommentSuccess?: () => void;
    onNoteCopySuccess?: (note: Dynamo.Note) => void;
    onNoteCopyToProfilesSuccess?: () => void;
    onNoteCopyToClientSuccess?: () => void;
    onLoadNoteSuccess?: () => void;
    onSendNoteEmailSuccess?: () => void;
    onNoteLinkToClientRecordSuccess?: (response: APITypes.notesService.LinkNoteToClientRecordResponse) => void;
};

export type User = APITypes.userService.User;

export type UpdateNoteArgs = {
    noteId: string;
    note: APITypes.notesService.NoteUpsert;
    drySubmit: boolean;
};

export type CreateNoteArgs = {
    note: APITypes.notesService.NoteUpsert;
    drySubmit: boolean;
};

export type LoadNoteFileArgs =
    | {
          tempFileId: string;
      }
    | {
          noteId: string;
          fileId: string;
      };

export type CommentArgs = {
    noteId: string;
    comment: string;
};

export type CopyNoteArgs = {
    sourceProfileId: string;
    destinationProfileId: string;
    noteId: string;
};

export type CopyNoteToProfilesArgs = {
    destinationMedicalProfileIds: string[];
    noteIds: string[];
    organizationId: string;
};

export type CopyOrgNotesToClientProfileArgs = {
    noteIds: string[];
    organizationId: string;
    destinationMedicalProfileId: string;
    destinationArgs: APITypes.notesService.CopyOrganizationNoteToMedicationProfileRequest;
};

export type SendNoteEmailArgs = {
    noteId: string;
    emails: string[];
};

export type LinkNoteToClientRecordArgs = {
    noteId: string;
};

export type UseNotesReturnValue = {
    isLoading: boolean;
    notes: NotesList;
    isFetchingNotes: boolean;
    isUpdatingUserNote: boolean;
    updateUserNote: (args: UpdateNoteArgs) => void;
    isDeletingNote: boolean;
    deleteNote: (noteId: string) => void;
    isDeletingNotes: boolean;
    deleteNotes: (noteIds: string[]) => void;
    isCreatingNote: boolean;
    createNote: (note: CreateNoteArgs) => void;
    isLoadingNote: boolean;
    note: APITypes.notesService.NoteDetails | null;
    loadNoteFile: (args: LoadNoteFileArgs) => void;
    isLoadingNoteFile: boolean;
    comment: (args: CommentArgs) => void;
    isCommenting: boolean;
    copyNote: (args: CopyNoteArgs) => void;
    isCopyingNote: boolean;
    copyNotesToProfiles: (args: CopyNoteToProfilesArgs) => void;
    isCopyingNoteToProfiles: boolean;
    copyOrgNotesToClientProfile: (args: CopyOrgNotesToClientProfileArgs) => void;
    isCopyingOrgNotesToClientProfile: boolean;
    isSendingNoteEmail: boolean;
    sendNoteEmail: (args: SendNoteEmailArgs) => void;
    linkNoteToClientRecord: (args: LinkNoteToClientRecordArgs) => void;
    isLinkingNoteToClientRecord: boolean;
};

type NoteDestinationWithResolvedResourceCategory = NoteDestination & {
    resourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory | null;
};

const getNotesCacheKey = (queryArgs: NoteQueryArgs): string[] | undefined => {
    if (queryArgs.entityType === 'user') {
        return getUserNotesCacheKey();
    }

    if (queryArgs.entityType === 'organization') {
        return getOrganizationNotesCacheKey();
    }

    if (queryArgs.entityType === 'organization-client-record') {
        return getOrganizationClientRecordNotesCacheKey(queryArgs.organizationClientRecordId);
    }

    if (queryArgs.entityType === 'care-team') {
        return getCareTeamMemberNotesCacheKey(queryArgs.medicalProfileId, '');
    }

    if (queryArgs.entityType === 'medical-profile') {
        return queryArgs.medicalProfileId ? getProfileNotesCacheKey(queryArgs.medicalProfileId) : undefined;
    }

    if (queryArgs.entityType === 'care-team-member') {
        // these entities have entity ids other than the medical profile id
        return getCareTeamMemberNotesCacheKey(queryArgs.medicalProfileId, queryArgs.entityId);
    } else if (
        queryArgs.entityType === 'condition-resource' ||
        queryArgs.entityType === 'medication-resource' ||
        queryArgs.entityType === 'allergy-resource' ||
        queryArgs.entityType === 'visit-resource' ||
        queryArgs.entityType === 'imaging-resource' ||
        queryArgs.entityType === 'vaccination-resource' ||
        queryArgs.entityType === 'procedure-resource' ||
        queryArgs.entityType === 'lab-resource'
    ) {
        if ('resourceId' in queryArgs) {
            // resource id is the entity id
            return getResourceNotesCacheKey(
                queryArgs.medicalProfileId,
                [queryArgs.resourceId, ...queryArgs.resourceGroupIds],
                queryArgs.entityType === 'condition-resource' ||
                    queryArgs.entityType === 'medication-resource' ||
                    queryArgs.entityType === 'allergy-resource' ||
                    queryArgs.entityType === 'visit-resource' ||
                    queryArgs.entityType === 'imaging-resource' ||
                    queryArgs.entityType === 'vaccination-resource' ||
                    queryArgs.entityType === 'procedure-resource' ||
                    queryArgs.entityType === 'lab-resource',
            );
        }
    } else {
        // other entities have the medical profile ID as the entity id
        return getResourceCategoryNotesCacheKey(queryArgs.medicalProfileId, queryArgs.entityType);
    }
};

type NotesQueryData =
    | {
          method: 'get';
          url: string;
      }
    | {
          method: 'post';
          url: string;
          body: any;
      }
    | 'missing-data';

const getNotesQueryData = (
    destination: NoteDestinationWithResolvedResourceCategory | undefined,
    queryArgs: NoteQueryArgs | undefined,
): NotesQueryData | null => {
    if (!destination || !queryArgs) return 'missing-data';

    if (queryArgs.entityType === 'user') {
        return { url: urls.api.notes.list.userNotes, method: 'get' };
    } else if (queryArgs.entityType === 'medical-profile') {
        return queryArgs.medicalProfileId
            ? { url: urls.api.notes.list.profileNotes(queryArgs.medicalProfileId), method: 'get' }
            : null;
    } else if (queryArgs.entityType === 'care-team') {
        return {
            url: urls.api.notes.list.careTeamNotes(queryArgs.medicalProfileId),
            method: 'get',
        };
    } else if (queryArgs.entityType === 'care-team-member') {
        return {
            url: urls.api.notes.list.careTeamMemberNotes(queryArgs.medicalProfileId, queryArgs.entityId),
            method: 'get',
        };
    } else if (queryArgs.entityType === 'organization') {
        return {
            url: urls.api.notes.list.organizationNotes(queryArgs.organizationId),
            method: 'get',
        };
    } else if (queryArgs.entityType === 'organization-client-record') {
        return {
            url: urls.api.notes.list.organizationClientRecordNotes(
                queryArgs.organizationId,
                queryArgs.organizationClientRecordId,
            ),
            method: 'get',
        };
    } else if (
        queryArgs.entityType === 'condition-resource' ||
        queryArgs.entityType === 'medication-resource' ||
        queryArgs.entityType === 'allergy-resource' ||
        queryArgs.entityType === 'visit-resource' ||
        queryArgs.entityType === 'imaging-resource' ||
        queryArgs.entityType === 'vaccination-resource' ||
        queryArgs.entityType === 'procedure-resource' ||
        queryArgs.entityType === 'lab-resource'
    ) {
        if (
            ('resourceId' in queryArgs && !queryArgs.resourceId) ||
            ('resourceGroupIds' in queryArgs && !queryArgs.resourceGroupIds?.length)
        )
            return 'missing-data';

        return {
            method: 'post',
            url: urls.api.notes.list.resourceGroupNotes(
                queryArgs.medicalProfileId!,
                resourceNoteEntityTypeToResourceCategory(queryArgs.entityType),
            ),
            body: {
                resourceGroupIds: [...new Set([queryArgs.resourceId, ...queryArgs.resourceGroupIds])],
            } as APITypes.notesService.GetResourceGroupNotesRequest,
        };
    } else {
        return {
            url: urls.api.notes.list.resourceTypeNotes(queryArgs.medicalProfileId, queryArgs.entityType),
            method: 'get',
        };
    }
};

const useResolveResourceCategory = (
    noteQueryArgs: NoteQueryArgs | null,
): NoteDestinationWithResolvedResourceCategory | null => {
    const { isLoading, resourceCategory } = useNoteQueryArgsResourceCategory(noteQueryArgs);
    if (isLoading) return null;

    return {
        ...noteQueryArgs,
        resourceCategory,
    } as NoteDestinationWithResolvedResourceCategory;
};

export const getDefaultNoteFromNoteDestination = (
    noteDestination: NoteDestination,
    userId: string,
): APITypes.notesService.NoteUpsert => {
    const medicalProfileId = 'medicalProfileId' in noteDestination ? noteDestination.medicalProfileId : undefined;

    const organizationId: string | undefined =
        noteDestination.entityType === 'organization' || noteDestination.entityType === 'organization-client-record'
            ? noteDestination.organizationId
            : undefined;

    const getEntityId = () => {
        if (noteDestination.entityType === 'user') {
            return userId;
        } else if (noteDestination.entityType === 'organization') {
            return noteDestination.organizationId;
        } else if (noteDestination.entityType === 'organization-client-record') {
            return noteDestination.organizationClientRecordId;
        } else if ('entityId' in noteDestination) {
            return noteDestination.entityId;
        } else {
            return noteDestination.medicalProfileId;
        }
    };

    return {
        title: '',
        content: '',
        tags: [],
        images: [],
        files: [],
        entityType: noteDestination.entityType,
        medicalProfileId: medicalProfileId || undefined,
        organizationId,
        entityId: getEntityId(),
    };
};

/**
 * Whether a loaded note's stored identity belongs to the given destination. Used to detect a stale
 * note bound to a reused editor (a note created for a previous profile/visit/resource). When it does
 * NOT match, the editor must create a fresh note on the current destination rather than update the
 * stale one.
 */
export const noteMatchesDestination = (
    note: APITypes.notesService.NoteDetails,
    noteDestination: NoteDestination,
    userId: string,
): boolean => {
    const expected = getDefaultNoteFromNoteDestination(noteDestination, userId);
    return (
        note.entityType === expected.entityType &&
        note.entityId === expected.entityId &&
        (note.medicalProfileId || undefined) === (expected.medicalProfileId || undefined) &&
        (note.organizationId || undefined) === (expected.organizationId || undefined)
    );
};

export default function useNotes({
    onUpdateSuccess,
    onDeletionSuccess,
    onCreationSuccess,
    onNoteLoadFailure,
    onLoadNoteFile,
    noteId,
    noteQueryArgs,
    onCommentSuccess,
    onNoteCopySuccess,
    onNoteCopyToProfilesSuccess,
    onCreateNoteResponse,
    onLoadNoteSuccess,
    onNoteCopyToClientSuccess,
    onSendNoteEmailSuccess,
    onNoteLinkToClientRecordSuccess,
}: UseNotesArgs): UseNotesReturnValue {
    const notify = useNotify();
    const queryClient = useQueryClient();
    const invalidateAllNotesQueries = onNotesUpdate.resetCacheWithDeferredParams();
    const isProfessionalApp = useExplicitAppType() === 'professional';
    // Null for consumers; used to record a newly saved copy on the cached source note without a refetch.
    const { myOrganization } = useOrganization({});
    const myOrganizationId = myOrganization?.organization.organizationId ?? null;
    const invalidateNoteCommentQueries = onNoteComment.resetCacheWithDeferredParams();
    const invalidateClientRecordNoteCopies = onClientRecordNoteCopiesChanged.resetCacheWithDeferredParams();
    const invalidateNoteLinks = onNoteLinksChanged.resetCacheWithDeferredParams();
    const resolvedNoteQueryArgs = useResolveResourceCategory(noteQueryArgs || null);
    const noteQueryData = getNotesQueryData(resolvedNoteQueryArgs!, noteQueryArgs);
    const notesQuery = useQuery({
        queryKey: (noteQueryArgs ? getNotesCacheKey(noteQueryArgs) : 'ignored-notes-cache-key') as any,
        queryFn: async () => {
            if (noteQueryData === 'missing-data' || !noteQueryData) return { notes: [] };

            if (noteQueryData.method === 'get') {
                return await appAPI.get(noteQueryData.url);
            } else {
                return await appAPI.post(noteQueryData.url, {
                    body: noteQueryData.body,
                });
            }
        },
        enabled: noteQueryData !== 'missing-data' && !!noteQueryData,
    });

    useQueryErrorNotify(notesQuery, intl('notesLoadFailure'));

    // The detail of a client-record copy is cached under the copy's own key, outside the client-record query root,
    // so a change to its source note refreshes each copy the source reports. Deferred: what a copy shows is read
    // through eventually consistent indexes.
    const invalidateCopyDetails = (copies: APITypes.notesService.NoteMirroredToTarget[] | undefined) => {
        for (const { mirrorNoteId } of copies ?? []) {
            invalidateNoteLinks({ noteId: mirrorNoteId });
        }
    };

    const createNoteMutation = useMutation({
        mutationFn: async (note: CreateNoteArgs) => {
            return await appAPI.post<APITypes.notesService.NoteDetails>(urls.api.notes.notes, {
                body: note.note,
            });
        },
        onSuccess: (response, input) => {
            invalidateAllNotesQueries({
                medicalProfileId: input.note.medicalProfileId || undefined,
                organizationClientRecordId:
                    input.note.entityType === 'organization-client-record' ? input.note.entityId : undefined,
            });
            // A new profile note may have been copied onto the organization's client record.
            if (isProfessionalApp && input.note.medicalProfileId) {
                invalidateClientRecordNoteCopies({});
            }
            notify('success', intl('createSuccess'));
            onCreationSuccess?.(input.drySubmit || false);
            onCreateNoteResponse?.(response);
        },
        onError: () => {
            notify('error', intl('createFailure'));
        },
    });

    const updateNoteMutation = useMutation({
        mutationFn: async ({ note, noteId }: UpdateNoteArgs) => {
            return await appAPI.put(urls.api.notes.note(noteId), {
                body: note,
            });
        },
        onSuccess: (_response, input) => {
            invalidateAllNotesQueries({
                medicalProfileId: input.note.medicalProfileId || undefined,
                organizationClientRecordId:
                    input.note.entityType === 'organization-client-record' ? input.note.entityId : undefined,
            });
            // Editing a profile note also updates any client-record copies the organization holds of it, and the
            // app does not know which records those are, so every mounted client-record query refetches on focus.
            if (isProfessionalApp && input.note.medicalProfileId) {
                invalidateClientRecordNoteCopies({});
            }
            // A copy's own detail is cached under its own key, so each copy this note reports is refreshed too.
            invalidateCopyDetails(noteQuery.data?.noteId === input.noteId ? noteQuery.data.mirroredTo : undefined);
            notify('success', intl('updateSuccess'));
            onUpdateSuccess?.(input.drySubmit || false);
        },
        onError: () => {
            notify('error', intl('updateFailure'));
        },
    });

    const noteQuery = useQuery({
        queryKey: getNoteCacheKey(noteId || ''),

        queryFn: async () => {
            return await appAPI.get<APITypes.notesService.NoteDetails>(urls.api.notes.note(noteId || ''));
        },
        enabled: !!noteId,
    });
    useQueryCallbacks({
        query: noteQuery,
        onError: () => {
            notify('error', 'noteLoadFailure');
            onNoteLoadFailure?.();
        },
        onSuccess: onLoadNoteSuccess,
    });

    const deleteNoteMutation = useMutation({
        mutationFn: async (noteId: string) => {
            return await appAPI.del(urls.api.notes.note(noteId));
        },
        onSuccess: (_data, noteId) => {
            const notesQueryMedicalProfileId = notesQuery.data?.notes.find((_: any) => _.noteId === noteId);
            const noteQueryMedicalProfileId = noteQuery.data?.medicalProfileId;
            invalidateAllNotesQueries({
                medicalProfileId: noteQueryMedicalProfileId || notesQueryMedicalProfileId || undefined,
                organizationClientRecordId:
                    noteQuery.data?.entityType === 'organization-client-record' ? noteQuery.data?.entityId : undefined,
            });
            // Deleting a source note turns its client-record copies from linked to retained.
            if (isProfessionalApp && (noteQueryMedicalProfileId || notesQueryMedicalProfileId?.medicalProfileId)) {
                invalidateClientRecordNoteCopies({});
            }
            invalidateCopyDetails(noteQuery.data?.noteId === noteId ? noteQuery.data.mirroredTo : undefined);
            // Deleting a client-record copy leaves its cached source note claiming a copy that no longer exists.
            const deletedCopy = noteQuery.data?.noteId === noteId ? noteQuery.data.mirror : undefined;
            if (deletedCopy) {
                queryClient.setQueryData<APITypes.notesService.NoteDetails>(
                    getNoteCacheKey(deletedCopy.sourceNoteId),
                    (cached) =>
                        cached
                            ? {
                                  ...cached,
                                  mirroredTo: (cached.mirroredTo ?? []).filter(
                                      (target) => target.mirrorNoteId !== noteId,
                                  ),
                              }
                            : cached,
                );
                if (deletedCopy.sourceMedicalProfileId) {
                    invalidateAllNotesQueries({ medicalProfileId: deletedCopy.sourceMedicalProfileId });
                }
            }
            notify('success', intl('deleteSuccess'));
            onDeletionSuccess?.();
        },
        onError: () => {
            notify('error', intl('deleteFailure'));
        },
    });

    const deleteNotesMutation = useMutation({
        mutationFn: async (noteIds: string[]) => {
            return await Promise.all(noteIds.map((_) => appAPI.del(urls.api.notes.note(_))));
        },
        onSuccess: (_data, noteIds) => {
            for (const noteId of noteIds) {
                const notesQueryMedicalProfileId = notesQuery.data?.notes.find((_: any) => _.noteId === noteId);
                const noteQueryMedicalProfileId = noteQuery.data?.medicalProfileId;
                invalidateAllNotesQueries({
                    medicalProfileId: noteQueryMedicalProfileId || notesQueryMedicalProfileId || undefined,
                    organizationClientRecordId:
                        noteQuery.data?.entityType === 'organization-client-record'
                            ? noteQuery.data?.entityId
                            : undefined,
                });
            }
            notify('success', intl('deleteNotesSuccess'));
            onDeletionSuccess?.();
        },
        onError: () => {
            notify('error', intl('deleteNotesFailure'));
        },
    });

    const loadNoteFileMutation = useMutation({
        mutationFn: async (args: LoadNoteFileArgs) => {
            if ('tempFileId' in args) {
                const response = await appAPI.get<APITypes.sharedTypes.GetAvatarResponse>(
                    urls.api.files.tempFile(args.tempFileId),
                );
                return response.avatarUrl;
            } else {
                const response = await appAPI.get<APITypes.notesService.GetNotFileDownloadUrlResponse>(
                    urls.api.notes.noteFile(args.noteId, args.fileId),
                );
                return response.downloadUrl;
            }
        },
        onSuccess: (url) => {
            onLoadNoteFile?.(url as string);
        },
        onError: () => {
            notify('error', intl('noteFileLoadFailure'));
        },
    });

    const comment = useMutation({
        mutationFn: async (args: CommentArgs) => {
            return await appAPI.post<NoteCommentMetadata>(urls.api.notes.noteComment(args.noteId), {
                body: {
                    comment: args.comment,
                },
            });
        },
        onSuccess: (response, args) => {
            queryClient.setQueriesData(
                { queryKey: getNoteCacheKey(args.noteId) },
                (oldData?: APITypes.notesService.NoteDetails) => {
                    // shouldn't happen
                    if (!oldData) return oldData as any;

                    return {
                        ...oldData,
                        comments: [...oldData.comments, response],
                    };
                },
            );

            if (args.noteId && noteQuery.data?.noteId === args.noteId) {
                invalidateNoteCommentQueries({
                    medicalProfileId:
                        noteQuery.data?.entityType !== 'user' ? noteQuery.data?.medicalProfileId : undefined,
                    organizationClientRecordId:
                        noteQuery.data?.entityType === 'organization-client-record'
                            ? noteQuery.data?.entityId
                            : undefined,
                });
                // A comment on a linked client-record copy is written on the profile note it tracks, so that
                // note and its profile's comment lists are stale too; the note's refetch waits for the comment index.
                const mirror = noteQuery.data?.mirror;
                if (mirror?.isLive) {
                    invalidateNoteLinks({ noteId: mirror.sourceNoteId });
                    invalidateNoteCommentQueries({ medicalProfileId: mirror.sourceMedicalProfileId ?? undefined });
                }
                // A professional's comment on a profile note reaches the organization's copies of it, or creates
                // one when there was none, so the copies' details and the client records refresh. A copy is found
                // through eventually consistent indexes, so the note's own refetch waits for them.
                if (isProfessionalApp && noteQuery.data?.medicalProfileId) {
                    invalidateCopyDetails(noteQuery.data.mirroredTo);
                    invalidateClientRecordNoteCopies({});
                    if (!noteQuery.data.mirroredTo?.length) {
                        invalidateNoteLinks({ noteId: args.noteId });
                        invalidateAllNotesQueries({ medicalProfileId: noteQuery.data.medicalProfileId });
                    }
                }
            }
            onCommentSuccess?.();
        },
        onError: (e) => {
            console.error(e);
            notify('error', intl('commentFailed'));
        },
    });

    const copyNoteMutation = useMutation({
        mutationFn: async (args: CopyNoteArgs) => {
            return await appAPI.post<APITypes.notesService.CopyToProfileResponse>(
                urls.api.notes.copyNote(args.sourceProfileId, args.noteId),
                {
                    body: {
                        destinationProfileId: args.destinationProfileId,
                    } as APITypes.notesService.CopyToProfileRequest,
                },
            );
        },
        onSuccess: (response) => {
            invalidateAllNotesQueries({ medicalProfileId: response.note.medicalProfileId });
            // A profile note created by a professional may have been copied onto the organization's client record.
            if (isProfessionalApp) {
                invalidateClientRecordNoteCopies({});
            }
            notify('success', intl('copyNoteSuccess'));
            onNoteCopySuccess?.(response.note);
        },
        onError: () => {
            notify('error', intl('copyNoteFailure'));
        },
    });

    const copyNoteToProfilesMutation = useMutation({
        mutationFn: async (args: CopyNoteToProfilesArgs) => {
            const body: APITypes.notesService.CopyOrganizationNoteToMedicalProfilesRequest = {
                destinationMedicalProfileIds: args.destinationMedicalProfileIds,
            };
            return await Promise.all(
                args.noteIds.map((noteId) => {
                    return appAPI.post(urls.api.notes.copyNoteToClients(args.organizationId, noteId), {
                        body,
                    });
                }),
            );
        },
        onSuccess: (_data, args) => {
            notify('success', intl('copyNoteToClientsSuccess'));
            onNoteCopyToProfilesSuccess?.();
            onOrgNoteCopiedToProfiles.resetCache({ profileIds: args.destinationMedicalProfileIds })();
            // A profile note created by a professional may have been copied onto the organization's client record.
            if (isProfessionalApp) {
                invalidateClientRecordNoteCopies({});
            }
        },
        onError: () => {
            notify('error', intl('copyNoteToClientsFailure'));
        },
    });

    const copyOrgNotesToClientProfileMutation = useMutation({
        mutationFn: async (args: CopyOrgNotesToClientProfileArgs) => {
            await Promise.all(
                args.noteIds.map((noteId) => {
                    const body: APITypes.notesService.CopyOrganizationNoteToMedicationProfileRequest =
                        args.destinationArgs;
                    return appAPI.post(
                        urls.api.notes.copyNoteToClient(args.organizationId, noteId, args.destinationMedicalProfileId),
                        {
                            body,
                        },
                    );
                }),
            );
        },
        onSuccess: (_data, args) => {
            notify('success', intl('copyNoteToClientSuccess'));
            onNoteCopyToClientSuccess?.();
            onOrgNoteCopiedToProfiles.resetCache({ profileIds: [args.destinationMedicalProfileId] })();
            // A profile note created by a professional may have been copied onto the organization's client record.
            if (isProfessionalApp) {
                invalidateClientRecordNoteCopies({});
            }
        },
        onError: () => {
            notify('error', intl('copyNoteToClientFailure'));
        },
    });

    const sendNoteEmailMutation = useMutation({
        mutationFn: async (args: SendNoteEmailArgs) => {
            const request: APITypes.notesService.SendNoteEmailRequest = {
                emailAddresses: args.emails,
            };
            return await appAPI.post(urls.api.notes.sendNoteEmail(args.noteId), {
                body: request,
            });
        },
        onSuccess: () => {
            notify('success', intl('sendNoteEmailSuccess'));
            onSendNoteEmailSuccess?.();
        },
        onError: () => {
            notify('error', intl('sendNoteEmailFailure'));
        },
    });

    // Linking creates (or finds) the organization-client-record copy of a profile note. The source note is
    // re-read afterwards so its list of client records holding a copy is current, and each client record the
    // response names has its notes list refreshed so the copy shows up there.
    // The source note's cached detail is patched rather than refetched: the index a refetch would read is
    // eventually consistent, so a refetch could cache the note as still unsaved. The client record's notes list is
    // reset the usual (deferred) way.
    const recordLinkedNotes = (
        links: { noteId: string; organizationClientRecordId: string; mirrorNoteId: string }[],
    ) => {
        for (const { noteId: linkedNoteId, organizationClientRecordId, mirrorNoteId } of links) {
            queryClient.setQueryData<APITypes.notesService.NoteDetails>(getNoteCacheKey(linkedNoteId), (cached) =>
                cached && myOrganizationId
                    ? {
                          ...cached,
                          mirroredTo: [
                              ...(cached.mirroredTo ?? []).filter(
                                  (target) => target.organizationClientRecordId !== organizationClientRecordId,
                              ),
                              { organizationId: myOrganizationId, organizationClientRecordId, mirrorNoteId },
                          ],
                      }
                    : cached,
            );
            // The profile's note lists hold their own copy of the note, so they refresh too (deferred, as the
            // index they read is eventually consistent).
            const medicalProfileId = queryClient.getQueryData<APITypes.notesService.NoteDetails>(
                getNoteCacheKey(linkedNoteId),
            )?.medicalProfileId;
            if (medicalProfileId) invalidateAllNotesQueries({ medicalProfileId });
        }
        for (const organizationClientRecordId of new Set(links.map((link) => link.organizationClientRecordId))) {
            invalidateAllNotesQueries({ organizationClientRecordId });
        }
    };

    const linkNoteToClientRecordMutation = useMutation({
        mutationFn: async (args: LinkNoteToClientRecordArgs) => {
            return await appAPI.post<APITypes.notesService.LinkNoteToClientRecordResponse>(
                urls.api.notes.linkNoteToClientRecord(args.noteId),
            );
        },
        onSuccess: (response, args) => {
            recordLinkedNotes([
                {
                    noteId: args.noteId,
                    organizationClientRecordId: response.organizationClientRecordId,
                    mirrorNoteId: response.mirrorNoteId,
                },
            ]);
            notify(
                'success',
                intl(
                    response.result === 'already-linked'
                        ? 'linkNoteToClientRecordAlreadyLinked'
                        : 'linkNoteToClientRecordSuccess',
                ),
            );
            onNoteLinkToClientRecordSuccess?.(response);
        },
        onError: (error: any) => {
            // A 400 means the caller's organization holds no client record for the note's profile.
            notify(
                'error',
                intl(
                    error?.response?.statusCode === 400
                        ? 'linkNoteToClientRecordNoClientRecord'
                        : 'linkNoteToClientRecordFailure',
                ),
            );
        },
    });

    return {
        isLoading: notesQuery.isLoading,
        notes: notesQuery.data || { notes: [] },
        isFetchingNotes: notesQuery.isFetching,
        isUpdatingUserNote: updateNoteMutation.isPending,
        updateUserNote: updateNoteMutation.mutate,
        isDeletingNote: deleteNoteMutation.isPending,
        deleteNotes: deleteNotesMutation.mutate,
        isDeletingNotes: deleteNotesMutation.isPending,
        deleteNote: deleteNoteMutation.mutate,
        isCreatingNote: createNoteMutation.isPending,
        createNote: createNoteMutation.mutate,
        isLoadingNote: noteQuery.isLoading,
        note: noteQuery.data || null,
        loadNoteFile: loadNoteFileMutation.mutate,
        isLoadingNoteFile: loadNoteFileMutation.isPending,
        comment: comment.mutate,
        isCommenting: comment.isPending,
        copyNote: copyNoteMutation.mutate,
        isCopyingNote: copyNoteMutation.isPending,
        copyNotesToProfiles: copyNoteToProfilesMutation.mutate,
        isCopyingNoteToProfiles: copyNoteToProfilesMutation.isPending,
        copyOrgNotesToClientProfile: copyOrgNotesToClientProfileMutation.mutate,
        isCopyingOrgNotesToClientProfile: copyOrgNotesToClientProfileMutation.isPending,
        isSendingNoteEmail: sendNoteEmailMutation.isPending,
        sendNoteEmail: sendNoteEmailMutation.mutate,
        linkNoteToClientRecord: linkNoteToClientRecordMutation.mutate,
        isLinkingNoteToClientRecord: linkNoteToClientRecordMutation.isPending,
    };
}

export type UseEditNoteArgs = Omit<UseNotesArgs, 'onCreationSuccess' | 'onUpdateSuccess' | 'onCreateNoteResponse'> & {
    onCreationSuccess?: () => void;
    onUpdateSuccess?: () => void;
    isDisabled?: boolean;
    /**
     * The note's target destination. A create-latch note whose stored identity doesn't match this
     * destination (a stale note bound to a reused editor host across a different visit/resource/
     * profile) is ignored rather than loaded, so the next save creates a fresh note on the current
     * destination instead of silently updating the note created for the previous one.
     */
    noteDestination?: NoteDestination;
};

export type UseEditNoteEditorProps = Pick<
    NoteEditorProps,
    'onSubmit' | 'note' | 'isDisabled' | 'isLoading' | 'ignoredStaleNote'
>;

export type UseEditNoteReturnValue = UseNotesReturnValue & {
    noteEditorProps: UseEditNoteEditorProps;
    key: string;
};

/**
 * Helper function to assist with dry submit logic for the note editor
 */
export const useEditNote = ({
    noteId,
    onCreationSuccess,
    onUpdateSuccess,
    noteDestination,
    ...args
}: UseEditNoteArgs): UseEditNoteReturnValue => {
    const [isDrySubmitRefreshing, setIsDrySubmitRefreshing] = React.useState<boolean>(false);
    const isCreate = !noteId;
    const [key, setKey] = React.useState<number>(0);
    const [maybeCreatedDrySubmitNote, setMaybeCreatedDrySubmitNote] =
        React.useState<APITypes.notesService.NoteDetails | null>(null);
    // When the target destination changes for a still-mounted editor host, drop the create-latch (and
    // bump the key) so the next submit creates a new note on the new destination rather than updating
    // the note that was created for the previous destination.
    const destinationKey = noteDestination ? JSON.stringify(noteDestination) : '';
    const previousDestinationKeyRef = React.useRef(destinationKey);
    React.useEffect(() => {
        if (previousDestinationKeyRef.current !== destinationKey) {
            previousDestinationKeyRef.current = destinationKey;
            // Intentionally do NOT clear the create-latch here. The gate below already ignores a latch
            // note that doesn't match the current destination (so it can't misroute), and keeping it
            // lets the next save report the prevented misroute via `ignoredStaleNote`. Clearing it here
            // would drop that signal before the user saves on the new destination. Just clear any
            // in-flight dry-submit refresh — an ignored stale dry-create starts no note query, so
            // onLoadNoteSuccess would never fire and the new destination's editor would stay stuck
            // loading — and bump the key for a clean remount.
            setIsDrySubmitRefreshing(false);
            setKey((prev) => prev + 1);
        }
    }, [destinationKey]);
    const { user } = useLoadedUser({});
    // Ignore a create-latch note that belongs to a different destination than where the editor now
    // points (a stale note bound to a reused editor host, including one re-armed by an in-flight create
    // that resolved after the destination changed). When stale, don't load it — so the next save
    // creates a fresh note on the current destination instead of updating the stale one — and surface
    // it for misroute instrumentation.
    const latchMatchesDestination =
        maybeCreatedDrySubmitNote && noteDestination && user?.userId
            ? noteMatchesDestination(maybeCreatedDrySubmitNote, noteDestination, user.userId)
            : true;
    const ignoredStaleNote =
        maybeCreatedDrySubmitNote && !latchMatchesDestination ? maybeCreatedDrySubmitNote : undefined;
    const useNotesNoteId = (ignoredStaleNote ? undefined : maybeCreatedDrySubmitNote?.noteId) || noteId;
    const useNotesRes = useNotes({
        ...args,
        onCreateNoteResponse: setMaybeCreatedDrySubmitNote,
        noteId: useNotesNoteId,
        onLoadNoteSuccess: () => {
            setIsDrySubmitRefreshing(false);
        },
        onCreationSuccess: (drySubmit) => {
            if (drySubmit) {
                setKey((prev) => prev + 1);
                return;
            }
            onCreationSuccess?.();
        },
        onUpdateSuccess: (drySubmit) => {
            if (drySubmit) {
                setKey((prev) => prev + 1);
                return;
            }
            // if the caller of this hook is anticipating a create because there was no existing note, call the onCreationSuccess
            isCreate ? onCreationSuccess?.() : onUpdateSuccess?.();
        },
    });

    return {
        noteEditorProps: {
            onSubmit: (args: SubmitArgs) => {
                if (args.drySubmit) {
                    setIsDrySubmitRefreshing(true);
                }
                (isCreate && !useNotesRes.note) || !useNotesRes.note?.noteId
                    ? useNotesRes.createNote(args)
                    : useNotesRes.updateUserNote({
                          note: args.note,
                          noteId: useNotesRes.note!.noteId,
                          drySubmit: args.drySubmit,
                      });
            },
            note: useNotesRes.note || undefined,
            ignoredStaleNote,
            isDisabled: args.isDisabled || useNotesRes.isCreatingNote || useNotesRes.isUpdatingUserNote,
            isLoading:
                useNotesRes.isCreatingNote ||
                useNotesRes.isUpdatingUserNote ||
                useNotesRes.isLoadingNote ||
                isDrySubmitRefreshing,
        },
        key: `${key}-${destinationKey}-${JSON.stringify(useNotesRes.note || {})}`,
        ...useNotesRes,
    };
};
